import {SideObject} from '@popperjs/core';
import React, {FC} from 'react';
import styled from 'styled-components';

import {RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CalendarsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {RepositionPopover} from '../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {isEventEditable} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {CalendarEventVersionResponse} from '../../../models/api/calendarEventVersionModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarEventViewerContentContainer} from './calendarEventViewerContentContainer';
import {CalendarEventViewerFooterContainer} from './calendarEventViewerFooterContainer';
import {PopoverCalendarEventViewerHeaderContainer} from './popoverCalendarEventViewerHeaderContainer';

/*
 * Props.
 */

interface PopoverCalendarEventViewerProps {
  panelId: CalendarsPanelId;
  anchor: Anchor;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  onRequestClose: () => void;
}

/*
 * Style.
 */

export const StyledLayoutDiv = styled.div`
  margin: 10px;
  width: 400px;
  max-height: calc(100vh - 80px);
  max-height: calc(100svh - 80px);

  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;

  border-radius: 6px;
  background-color: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
`;

export const StyledContentScrollerDiv = styled.div`
  overflow-y: auto;
`;

export const StyledContentDiv = styled.div`
  padding: 16px 20px;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
  word-break: break-word;
`;

const StyledFooterDiv = styled.div`
  position: sticky;
  bottom: 0px;

  display: grid;
  grid-row-gap: 12px;
  padding: 16px;

  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  background-color: ${(p) => p.theme.foreground};
`;

// Partially fixes https://front.atlassian.net/browse/PB-36340
const popoverCalendarEventOverflow: SideObject = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

/*
 * Component.
 */

export const PopoverCalendarEventViewer: FC<PopoverCalendarEventViewerProps> = (props) => {
  const {panelId, calendarEventData, visualParticipants, onRequestClose} = props;
  const {calendarEvent, calendarEventVersion, calendar, calendarVersion} = calendarEventData;
  const {response} = calendarEventVersion;
  const isCancelled = calendarEvent.status === CalendarEventStatusesEnum.CANCELLED;
  const canEditResponseStatus = isEventEditable(calendar, calendarVersion, calendarEventVersion);

  // Per product requirements, we explicitly choose to move focus away from the event after
  // responding to a recurring event (when the user is prompted to select a recurring save type).
  // This is to alleviate possible visual lag when rsvping to a "different" event (the instance from
  // the master or vice versa).
  const renderFooter: RendererOf<CalendarEventVersionResponse> = (r) => (
    <StyledFooterDiv>
      <CalendarEventViewerFooterContainer
        calendarEventData={calendarEventData}
        response={r}
        onRecurringSaveTypeSelection={onRequestClose}
      />
    </StyledFooterDiv>
  );

  return (
    <RepositionPopover
      preventOverflowPadding={popoverCalendarEventOverflow}
      anchor={props.anchor}
      placement="right"
    >
      <StyledLayoutDiv>
        <PopoverCalendarEventViewerHeaderContainer
          panelId={panelId}
          calendarEventData={calendarEventData}
          visualParticipants={visualParticipants}
          onRequestClose={onRequestClose}
        />
        <StyledContentScrollerDiv>
          <StyledContentDiv>
            <CalendarEventViewerContentContainer
              panelId={panelId}
              calendarEventData={calendarEventData}
              visualParticipants={visualParticipants}
            />
          </StyledContentDiv>
        </StyledContentScrollerDiv>
        {response && canEditResponseStatus && !isCancelled && renderFooter(response)}
      </StyledLayoutDiv>
    </RepositionPopover>
  );
};
